import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, Phone, ArrowRight, Factory, Cog, Flame, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';

const FactoryEPC = () => {
  const faqItems = [
    {
      question: 'Do factories require an EPC certificate?',
      answer: 'Yes, all factories in England require an Energy Performance Certificate when being sold, let or constructed. This includes manufacturing plants, production facilities, food processing factories and any industrial premises over 50m² that use energy for heating, lighting or processes.'
    },
    {
      question: 'How is a factory EPC different from other commercial EPCs?',
      answer: 'Factory EPCs must account for complex heating systems, process ventilation, high energy lighting and potentially multiple zones with different uses. Our assessors use SBEM methodology to model these complexities accurately and produce compliant certificates.'
    },
    {
      question: 'Does process equipment affect the EPC rating?',
      answer: 'No, process equipment energy use is not included in the EPC assessment. The EPC rates the building itself—its fabric, fixed heating systems, lighting and ventilation—not the manufacturing processes or equipment within it. This means the rating reflects the property, not the business operations.'
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": { "@type": "Answer", "text": item.answer }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Factory EPC Assessments | Manufacturing Plant EPCs | South West UK</title>
        <meta name="description" content="Professional EPC assessments for factories and manufacturing plants across Devon, Cornwall, Somerset and Dorset. SBEM certified assessors for production facilities." />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/factory-epc" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      
      <Navbar />
      <Breadcrumbs items={[{ label: 'Industrial EPC', path: '/industrial-epc' }, { label: 'Factory EPC', path: '/factory-epc' }]} />
      
      <section className="bg-gradient-to-br from-slate-800 via-slate-700 to-slate-800 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-slate-500/20 text-slate-300 px-4 py-2 rounded-full text-sm mb-6">
              <Factory className="w-4 h-4" />
              Factory EPC Specialists
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Factory EPC Assessments</h1>
            <p className="text-xl text-slate-300 mb-4">Manufacturing Plants • Production Facilities • Processing Units</p>
            <p className="text-lg text-slate-400 mb-8 max-w-3xl mx-auto">
              Expert Energy Performance Certificates for factories and manufacturing plants across Devon, Cornwall, Somerset and Dorset. Our accredited assessors understand the complexities of production facility energy assessment.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-green-600 hover:bg-green-700">
                <Link to="/booking?service=commercial_warehouse">Get Factory EPC Quote <ArrowRight className="ml-2 w-5 h-5" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                <a href="tel:01392980077"><Phone className="mr-2 w-5 h-5" />Call 01392 980077</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Energy Performance Certificates for Factories</h2>
            <p className="text-gray-700">
              Factories and manufacturing plants represent some of the most complex buildings for energy assessment. These facilities often combine large production halls with office areas, storage zones, loading bays and staff facilities, each with different heating, lighting and ventilation requirements.
            </p>
            <p className="text-gray-700">
              Our accredited non-domestic energy assessors have extensive experience assessing factories across the South West. From food processing plants requiring strict temperature control to engineering works with localised heating, we understand how to accurately model factory energy performance using SBEM methodology.
            </p>
            <p className="text-gray-700">
              It's important to note that factory EPCs assess the building's inherent energy efficiency—not the energy consumed by manufacturing processes or equipment. The rating reflects the building fabric, fixed heating systems, lighting and ventilation, providing a standardised measure that allows comparison between properties.
            </p>
            
            <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">Factory Types We Assess</h2>
            <div className="grid md:grid-cols-2 gap-4 not-prose">
              {[
                { icon: Factory, title: 'Manufacturing Plants', desc: 'General manufacturing and production facilities' },
                { icon: Cog, title: 'Engineering Works', desc: 'Precision engineering and fabrication factories' },
                { icon: Flame, title: 'Food Processing', desc: 'Food production, bakeries and beverage facilities' },
                { icon: Zap, title: 'Assembly Plants', desc: 'Assembly lines and component manufacturing' }
              ].map((item, i) => (
                <Card key={i}>
                  <CardContent className="p-4 flex items-start gap-4">
                    <item.icon className="w-8 h-8 text-slate-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold">{item.title}</h4>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">Factors Affecting Factory EPC Ratings</h2>
            <p className="text-gray-700">
              Factory energy performance depends on several key factors that our assessors examine:
            </p>
            <ul className="text-gray-700">
              <li><strong>Building Envelope:</strong> Roof and wall insulation, cladding type, glazing ratios and air permeability of the main production areas</li>
              <li><strong>Heating Systems:</strong> Radiant heaters, warm air systems, unit heaters and any localised heating for specific zones</li>
              <li><strong>Lighting:</strong> High-bay production lighting, office lighting, emergency lighting and natural daylight provision</li>
              <li><strong>Ventilation:</strong> Process ventilation, general ventilation, heat recovery systems and air handling units</li>
              <li><strong>Ancillary Areas:</strong> Office blocks, staff facilities, canteens and storage areas within the factory complex</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Factory EPC FAQs</h2>
            <div className="space-y-6">
              {faqItems.map((faq, index) => (
                <div key={index} className="bg-white rounded-lg p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Services</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <Link to="/industrial-epc" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100">
                <h3 className="font-semibold">Industrial EPCs</h3>
                <p className="text-sm text-gray-600">All industrial building types</p>
              </Link>
              <Link to="/warehouse-epc" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100">
                <h3 className="font-semibold">Warehouse EPCs</h3>
                <p className="text-sm text-gray-600">Distribution centres</p>
              </Link>
              <Link to="/workshop-epc" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100">
                <h3 className="font-semibold">Workshop EPCs</h3>
                <p className="text-sm text-gray-600">Light industrial units</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-green-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Get Your Factory EPC Quote</h2>
          <p className="text-xl text-green-100 mb-8">Fast, professional assessments across Devon, Cornwall, Somerset and Dorset.</p>
          <Button asChild size="lg" className="bg-white text-green-600 hover:bg-gray-100">
            <Link to="/booking?service=commercial_warehouse">Request Quote <ArrowRight className="ml-2 w-5 h-5" /></Link>
          </Button>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default FactoryEPC;